import {useRef, type ReactNode} from 'react'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

import BackgroundGrid from '../BackgroundGrid/BackgroundGrid'

import Styles from './Product.module.css'
import {ProductMediaLoadingContext} from './product-media-loading-context'

type ProductPlacement = 'bottom' | 'center' | 'centerShadow' | 'right' | 'rightBottom' | 'rightShadow'
type ProductVariant = 'media' | 'river'

type Props = {
  children: ReactNode
  placement?: string
  variant?: ProductVariant
}

function getProductPlacement(placement?: string): ProductPlacement | undefined {
  return placement === 'bottom' ||
    placement === 'center' ||
    placement === 'centerShadow' ||
    placement === 'right' ||
    placement === 'rightBottom' ||
    placement === 'rightShadow'
    ? placement
    : undefined
}

export function Product({children, placement, variant}: Props) {
  const productRef = useRef<HTMLDivElement>(null)
  const productPlacement = getProductPlacement(placement)
  const contentAnimation = placement ? 'slide' : 'scale'
  const contentAnimationThreshold = contentAnimation === 'scale' ? 0 : 0.33
  const hasShadow = productPlacement === 'centerShadow' || productPlacement === 'rightShadow'
  const {isIntersecting: shouldLoadMedia} = useIntersectionObserver(productRef, {
    isOnce: true,
    rootMargin: '1000px 0px',
    threshold: 0,
  })
  const {isIntersecting} = useIntersectionObserver(productRef, {
    isOnce: true,
    threshold: contentAnimationThreshold,
  })

  return (
    <ProductMediaLoadingContext value={shouldLoadMedia}>
      <div
        ref={productRef}
        className={Styles.Product}
        data-content-animation={contentAnimation}
        data-content-visible={isIntersecting ? true : undefined}
        data-placement={productPlacement}
        data-shadow-visible={hasShadow && isIntersecting ? true : undefined}
        data-variant={variant}
      >
        <div className={Styles.BackgroundGrid}>
          <BackgroundGrid />
        </div>
        <div className={Styles.Content}>{children}</div>
      </div>
    </ProductMediaLoadingContext>
  )
}
